import React, { useState, useEffect } from 'react';
import { useLanguage } from '../App';
import { progressAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, TrendingUp, Minus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const METRIC_TYPES = ['overall_score', 'weight_metric', 'fitness', 'grooming', 'style_metric', 'confidence_metric'];
const METRIC_COLORS = {
  overall_score: 'var(--lux-gold)',
  weight_metric: 'hsl(182,55%,46%)',
  fitness: 'hsl(210,60%,56%)',
  grooming: 'hsl(25,80%,60%)',
  style_metric: 'hsl(280,35%,62%)',
  confidence_metric: 'var(--lux-success)'
};

export default function ProgressPage() {
  const { language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('overall_score');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({ metric_type: 'overall_score', value: '', notes: '' });

  useEffect(() => { loadEntries(); }, []);

  const loadEntries = async () => {
    try {
      const res = await progressAPI.list();
      setEntries(res.data.entries || []);
    } catch (err) {
      console.error('Failed to load progress entries:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.value) return;
    try {
      await progressAPI.add(newEntry.metric_type, parseFloat(newEntry.value), newEntry.notes);
      toast.success(language === 'fr' ? 'Entr\u00e9e ajout\u00e9e!' : 'Entry added!');
      setNewEntry({ metric_type: 'overall_score', value: '', notes: '' });
      setDialogOpen(false);
      loadEntries();
    } catch (err) {
      toast.error('Error adding entry');
    }
  };

  const filteredEntries = entries
    .filter(e => e.metric_type === selectedMetric)
    .reverse();
  
  const chartData = filteredEntries.map(e => ({
    date: new Date(e.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' }),
    value: e.value
  }));

  // Calculate trend
  const latestValues = filteredEntries.slice(-2);
  const trend = latestValues.length === 2 ? latestValues[1].value - latestValues[0].value : 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl text-white">{t('progress', language)}</h1>
            <p className="text-white/50 text-sm mt-1">
              {language === 'fr' ? 'Suivez votre progression' : 'Track your progress'}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-progress-entry-button"
                className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('addEntry', language)}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--lux-panel)] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white font-display">{t('addEntry', language)}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs">{t('metricType', language)}</Label>
                  <Select value={newEntry.metric_type} onValueChange={v => setNewEntry(prev => ({ ...prev, metric_type: v }))}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--lux-panel)] border-white/10">
                      {METRIC_TYPES.map(m => (
                        <SelectItem key={m} value={m} className="text-white hover:bg-white/5">{t(m, language)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs">{t('value', language)}</Label>
                  <Input
                    data-testid="progress-value-input"
                    type="number"
                    value={newEntry.value}
                    onChange={e => setNewEntry(prev => ({ ...prev, value: e.target.value }))}
                    className="bg-black/20 border-white/10 text-white"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs">{t('notes', language)}</Label>
                  <Input
                    value={newEntry.notes}
                    onChange={e => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-black/20 border-white/10 text-white"
                    placeholder={language === 'fr' ? 'Notes optionnelles...' : 'Optional notes...'}
                  />
                </div>
                <Button
                  data-testid="progress-submit-button"
                  onClick={handleAddEntry}
                  className="w-full bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)]"
                >
                  {t('addEntry', language)}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Metric Tabs */}
      <Tabs value={selectedMetric} onValueChange={setSelectedMetric} data-testid="progress-metric-tabs">
        <TabsList className="bg-[var(--lux-panel)] border border-white/[0.08] flex-wrap h-auto gap-1 p-1">
          {METRIC_TYPES.map(m => (
            <TabsTrigger key={m} value={m} className="text-xs data-[state=active]:bg-lux-gold/15 data-[state=active]:text-[var(--lux-amber)]">
              {t(m, language)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Chart */}
      <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="progress-chart">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-display text-white">{t(selectedMetric, language)}</CardTitle>
            {trend !== 0 && (
              <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-[var(--lux-success)]' : 'text-[var(--lux-danger)]'}`}>
                {trend > 0 ? <TrendingUp className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                {trend > 0 ? '+' : ''}{trend.toFixed(1)}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={METRIC_COLORS[selectedMetric]} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={METRIC_COLORS[selectedMetric]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.08)' }} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.08)' }} />
                <Tooltip contentStyle={{ background: 'var(--lux-panel)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                <Area type="monotone" dataKey="value" stroke={METRIC_COLORS[selectedMetric]} fill="url(#areaGrad)" strokeWidth={2} dot={{ r: 4, fill: METRIC_COLORS[selectedMetric] }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <TrendingUp className="w-8 h-8 text-white/10 mb-3" />
              <p className="text-white/30 text-sm">{t('noProgress', language)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Entries */}
      {filteredEntries.length > 0 && (
        <Card className="bg-lux-panel/90 border-white/[0.08]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white/60">
              {language === 'fr' ? 'Entr\u00e9es r\u00e9centes' : 'Recent Entries'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {filteredEntries.slice(0, 10).map((entry, i) => (
                <div key={entry._id || i} className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                  <div>
                    <span className="text-white font-semibold tabular-nums">{entry.value}</span>
                    {entry.notes && <span className="text-white/40 text-xs ml-2">{entry.notes}</span>}
                  </div>
                  <span className="text-white/30 text-xs">
                    {new Date(entry.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
